import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Layout, Form, Input, Button, Radio, Typography, message, notification, QRCode, Menu, Card, Tag, Space, Modal, Grid, Tabs } from 'antd';
import { LockOutlined, CheckCircleOutlined, SecurityScanOutlined, UserOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import CustomNavbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const { Text } = Typography;
const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const Settings = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    const [selectedSetting, setSelectedSetting] = useState('mfa');
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [mfaMethod, setMfaMethod] = useState(null);
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [navbarReady, setNavbarReady] = useState(false);
    const [availableMethods, setAvailableMethods] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isDisableMfaModalVisible, setIsDisableMfaModalVisible] = useState(false);
    const [isDeleteAccountModalVisible, setIsDeleteAccountModalVisible] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchActiveMfaMethods = async () => {
            try {
                const response = await api.get('/mfa/active-mfa');
                if (response.data.status === 'success') {
                    const methods = response.data.data.map((method) => method.name);
                    setAvailableMethods(methods);
                    if (methods.length > 0) {
                        setMfaMethod(methods[0]);
                    }
                }
            } catch (err) {
                message.error('Failed to load MFA methods.');
            }
        };

        fetchActiveMfaMethods();
    }, []);

    useEffect(() => {
        const fetchMfaStatus = async () => {
            try {
                const response = await api.get('/mfa/mfa-status');
                if (response.data.status === 'success') {
                    const { mfa_enabled, mfa_type } = response.data.data;
                    setMfaEnabled(mfa_enabled);
                    setMfaMethod(mfa_type);
                }
            } catch (err) {
                message.error('Failed to fetch MFA status.');
            }
        };

        fetchMfaStatus();
    }, []);

    useEffect(() => {
        const shouldDisable = !mfaMethod ||
            (mfaMethod === 'authenticator' && (!qrCode || !otp || otp.length !== 6));
        setIsButtonDisabled(shouldDisable);
    }, [mfaMethod, qrCode, otp]);

    const handleMenuClick = ({ key }) => {
        setSelectedSetting(key);
    };

    const handleMfaToggle = async (checked) => {
        if (!checked) {
            setIsDisableMfaModalVisible(true);
        }
    };

    const handleDisableMfa = async () => {
        try {
            const response = await api.post(
                '/mfa/disable',
                {}
            );

            if (response.status === 200 && response.data.status === 'success') {
                notification.success({
                    message: 'Settings Saved',
                    description: 'MFA disabled successfully.',
                });
                setMfaEnabled(false);
                setMfaMethod(null);
                setQrCode('');
                setSecret('');
                setOtp('');
            } else {
                message.error('Failed to disable MFA. Please try again.');
            }
        } catch (error) {
            message.error('An error occurred while disabling MFA.');
        } finally {
            setIsDisableMfaModalVisible(false);
        }
    };

    const handleMfaMethodChange = async (e) => {
        const method = e.target.value;
        setMfaMethod(method);

        if (method === 'authenticator') {
            try {
                const response = await api.post(
                    '/mfa/generate-secret',
                    { type: 'authenticator' }
                );

                if (response.status === 200 && response.data.status === 'success') {
                    setQrCode(response.data.data.qr_code_url);
                    setSecret(response.data.data.secret);
                } else {
                    message.error('Failed to generate QR code. Please try again.');
                }
            } catch (error) {
                message.error('An error occurred while generating the QR code.');
            }
        } else {
            setQrCode('');
            setSecret('');
        }
    };

    const handleSaveSettings = async () => {
        if (mfaMethod === 'authenticator' && (otp.length !== 6 || !secret)) {
            message.error('Please enter a valid 6-digit verification code and ensure the QR code is generated.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                type: mfaMethod,
                ...(mfaMethod === 'authenticator' && { otp, secret }),
            };

            const response = await api.post(
                '/mfa/enable',
                payload
            );

            if (response.status === 200 && response.data.status === 'success') {
                notification.success({
                    message: 'Settings Saved',
                    description: 'MFA has been successfully enabled.',
                });
                setMfaEnabled(true);
                setQrCode('');
                setSecret('');
                setOtp('');
            } else {
                notification.error({
                    message: 'Failed to save settings',
                    description: 'Please try again',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save settings',
                description: 'Please try again',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteLoading(true);
        try {
            const response = await api.delete('/account/delete');

            if (response.data.status === 'success') {
                notification.success({
                    message: 'Account Deleted',
                    description: 'Your account has been deleted successfully.',
                });
                localStorage.removeItem('user_id');
                localStorage.removeItem('first_name');
                localStorage.removeItem('last_name');
                navigate('/');
            } else {
                message.error('Failed to delete account. Please try again.');
            }
        } catch (error) {
            message.error('An error occurred while deleting your account.');
        } finally {
            setDeleteLoading(false);
            setIsDeleteAccountModalVisible(false);
        }
    };

    const menuItems = [
        {
            key: 'security',
            icon: <SecurityScanOutlined />,
            label: 'Security',
            children: [
                {
                    key: 'mfa',
                    label: 'Multi-Factor Authentication',
                },
            ],
        },
        {
            key: 'account',
            icon: <UserOutlined />,
            label: 'Account',
            children: [
                {
                    key: 'delete-account',
                    label: 'Delete Account',
                },
            ],
        },
    ];

    const renderMfaSettings = () => (
        <Card
            style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}
            title={
                <Space>
                    <LockOutlined />
                    <span>Multi-Factor Authentication (MFA)</span>
                </Space>
            }
        >
            {mfaEnabled ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: '8px',
                        marginBottom: '24px'
                    }}>
                        <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                            <Text strong>MFA is currently enabled</Text>
                        </Space>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                        <Space wrap>
                            <Text strong>Authentication Method:</Text>
                            <Tag color="green" icon={<CheckCircleOutlined />}>
                                {mfaMethod?.charAt(0).toUpperCase() + mfaMethod?.slice(1)}
                            </Tag>
                        </Space>
                        <Button
                            type="primary"
                            danger
                            onClick={() => handleMfaToggle(false)}
                            style={{ flexShrink: 0 }}
                        >
                            Disable MFA
                        </Button>
                    </div>
                </Space>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <Text type="secondary">
                            Multi-Factor Authentication adds an extra layer of security to your account.
                            When enabled, you'll need to provide a verification code in addition to your password when signing in.
                        </Text>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <Text strong style={{ display: 'block', marginBottom: '16px' }}>Select Authentication Method:</Text>
                        <Radio.Group
                            onChange={handleMfaMethodChange}
                            value={mfaMethod}
                            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            {availableMethods.map((method) => (
                                <Radio
                                    key={method}
                                    value={method}
                                    style={{ width: 'fit-content' }}
                                >
                                    {method.charAt(0).toUpperCase() + method.slice(1)}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </div>

                    {mfaMethod === 'authenticator' && (
                        <>
                            <div style={{
                                marginBottom: '24px',
                                textAlign: 'center',
                                padding: '24px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '8px'
                            }}>
                                {qrCode ? (
                                    <>
                                        <QRCode value={qrCode} size={200} />
                                        <p style={{ marginTop: '16px', textAlign: 'left' }}>
                                            Scan this QR code with your authenticator app. If you don't have an authenticator app, you can download one from{' '}
                                            <a href="https://www.hirehike.com/2f-authenticator" target="_blank" rel="noopener noreferrer">
                                                here
                                            </a>
                                        </p>
                                    </>
                                ) : (
                                    <p>Generating QR code...</p>
                                )}
                            </div>
                            <Form layout="vertical">
                                <Form.Item
                                    label="Enter Verification Code"
                                    name="otp"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter the verification code from your authenticator app.',
                                        },
                                        {
                                            len: 6,
                                            message: 'Code must be exactly 6 digits.',
                                        },
                                    ]}
                                >
                                    <Input.OTP
                                        value={otp}
                                        onChange={(value) => setOtp(value)}
                                        length={6}
                                        isInputNum
                                        placeholder="Enter code"
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            gap: '8px',
                                        }}
                                        inputStyle={{
                                            width: '48px',
                                            height: '48px',
                                            fontSize: '16px',
                                            textAlign: 'center',
                                            borderRadius: '8px',
                                            border: '1px solid #d9d9d9',
                                        }}
                                        focusStyle={{
                                            border: '1px solid #AD1457',
                                            outline: 'none',
                                        }}
                                    />
                                </Form.Item>
                            </Form>
                        </>
                    )}

                    <div style={{ textAlign: 'right', marginTop: '24px' }}>
                        <Button
                            type="primary"
                            size="large"
                            loading={loading}
                            onClick={handleSaveSettings}
                            disabled={isButtonDisabled}
                        >
                            Enable MFA
                        </Button>
                    </div>
                </Space>
            )}
        </Card>
    );

    const renderDeleteAccountSettings = () => (
        <Card
            style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}
            title={
                <Space>
                    <DeleteOutlined style={{ color: '#AD1457' }} />
                    <span style={{ color: '#AD1457' }}>Delete Account</span>
                </Space>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{
                    padding: '16px',
                    backgroundColor: '#fdf2f7',
                    border: '1px solid rgba(173,20,87,0.2)',
                    borderRadius: '8px',
                    marginBottom: '24px',
                }}>
                    <Space align="start">
                        <ExclamationCircleOutlined style={{ color: '#AD1457', fontSize: '18px', marginTop: '2px' }} />
                        <div>
                            <Text strong style={{ color: '#AD1457', display: 'block', marginBottom: '4px' }}>
                                This action cannot be undone
                            </Text>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Deleting your account will remove all your profiles, matches, messages, and subscription data.
                                Your account will be deactivated immediately and permanently deleted after 30 days.
                            </Text>
                        </div>
                    </Space>
                </div>

                <div style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<DeleteOutlined />}
                        onClick={() => setIsDeleteAccountModalVisible(true)}
                    >
                        Delete My Account
                    </Button>
                </div>
            </Space>
        </Card>
    );

    const tabItems = [
        {
            key: 'mfa',
            label: <span><LockOutlined style={{ marginRight: 6 }} />Multi-Factor Auth</span>,
            children: (
                <div style={{ padding: isMobile ? '16px 0' : '0' }}>
                    {renderMfaSettings()}
                </div>
            ),
        },
        {
            key: 'delete-account',
            label: <span><DeleteOutlined style={{ marginRight: 6 }} />Delete Account</span>,
            children: (
                <div style={{ padding: isMobile ? '16px 0' : '0' }}>
                    {renderDeleteAccountSettings()}
                </div>
            ),
        },
    ];

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Helmet><title>Account Settings | Oottan</title></Helmet>
            <CustomNavbar onNavbarReady={() => setNavbarReady(true)} />
            {navbarReady && (
                isMobile ? (
                    /* ── Mobile / Tablet: stacked tabs layout ── */
                    <div style={{ marginTop: '64px', flex: 1, display: 'flex', flexDirection: 'column', background: '#fff' }}>
                        <div style={{
                            padding: '16px 16px 0',
                            borderBottom: '1px solid #f0f0f0',
                            background: '#fff',
                        }}>
                            <h2 style={{ margin: '0 0 12px', fontSize: '1.1rem', fontWeight: 700, color: '#1A1A2E' }}>
                                Account Settings
                            </h2>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 24px' }}>
                            <Tabs
                                activeKey={selectedSetting}
                                onChange={setSelectedSetting}
                                items={tabItems}
                                style={{ marginTop: 0 }}
                                tabBarStyle={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1, marginBottom: 0 }}
                            />
                        </div>
                    </div>
                ) : (
                    /* ── Desktop: sidebar + content ── */
                    <Layout style={{ marginTop: '64px', height: 'calc(100vh - 64px)' }}>
                        <Sider
                            width={280}
                            style={{
                                background: '#fff',
                                overflowY: 'auto',
                                height: '100%',
                                borderRight: '1px solid #f0f0f0',
                                padding: '16px 0',
                            }}
                        >
                            <Menu
                                mode="inline"
                                selectedKeys={[selectedSetting]}
                                defaultOpenKeys={['security', 'account']}
                                onClick={handleMenuClick}
                                items={menuItems}
                                style={{ height: '100%', borderRight: 'none' }}
                            />
                        </Sider>
                        <Content style={{ padding: '24px', background: '#fff', overflowY: 'auto', height: '100%' }}>
                            {selectedSetting === 'mfa' && renderMfaSettings()}
                            {selectedSetting === 'delete-account' && renderDeleteAccountSettings()}
                        </Content>
                    </Layout>
                )
            )}

            {/* Disable MFA Modal */}
            <Modal
                title="Disable Multi-Factor Authentication"
                open={isDisableMfaModalVisible}
                onOk={handleDisableMfa}
                onCancel={() => setIsDisableMfaModalVisible(false)}
                okText="Yes, Disable MFA"
                okType="danger"
                cancelText="Cancel"
                centered
            >
                <p>Are you sure you want to disable MFA? This will reduce the security of your account.</p>
            </Modal>

            {/* Delete Account Modal */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#AD1457' }} />
                        <span style={{ color: '#AD1457' }}>Delete Account</span>
                    </Space>
                }
                open={isDeleteAccountModalVisible}
                onOk={handleDeleteAccount}
                onCancel={() => setIsDeleteAccountModalVisible(false)}
                okText="Yes, Delete My Account"
                okButtonProps={{ loading: deleteLoading }}
                cancelText="Cancel"
                centered
            >
                <p>Are you sure you want to permanently delete your account? This action cannot be undone.</p>
                <p style={{ color: '#AD1457', fontWeight: '500' }}>All your profiles, matches, and data will be removed.</p>
            </Modal>
        </div>
    );
};

export default Settings;
